import { useEffect, useState, useRef } from "react";
import { getMeshStatus } from "@/lib/lanMesh";

/**
 * useOnlineStatus() — reactive `navigator.onLine` mirror + LAN-aware
 * override.
 *
 * Session 27.96 — Redefined "online" to mean "connected to the local
 * restaurant network", not "reachable on the public internet". This
 * matches the operator's mental model: if the Wi-Fi is up and my
 * tablet can talk to the kitchen/coordinator over LAN, the app is
 * NOT offline — even if the internet is out — because the restaurant
 * can keep taking orders and syncing to cloud later.
 *
 * Session 27.140 — Hardened against false "Offline Mode" alerts.
 * Changes from v1:
 *   • Increased health-check timeout 8s → 15s (heavy-load tolerance)
 *   • Raised failStreak threshold 2 → 4 (~60s of real failures)
 *   • Added 3s debounce on navigator offline events (Android
 *     WebViews fire `offline` spuriously on sleep/wake cycles)
 *   • Recovery requires 2 consecutive successes (prevents flicker)
 *   • LAN mesh rechecked after debounce delay, not just at event time
 *
 * Order of authority (highest → lowest):
 *   1. LAN mesh: if `getMeshStatus().status === "lan"` we are
 *      DEFINITIVELY online. WebRTC data channels are open with at
 *      least one peer — the restaurant network is healthy.
 *   2. `navigator.onLine === false` → offline. The browser (or the
 *      OS on native) is telling us Wi-Fi is disconnected.
 *   3. Backend health heartbeat as a fallback for machines where
 *      `navigator.onLine` misreports and the mesh hasn't kicked in
 *      yet.
 *
 * Returns { online: boolean, source: "nav" | "heartbeat" | "lan" }.
 */

const HEALTH_TIMEOUT_MS = 15000;
const HEARTBEAT_INTERVAL_MS = 15000;
const OFFLINE_DEBOUNCE_MS = 3000;
const FAIL_STREAK_THRESHOLD = 4;
const RECOVERY_SUCCESSES_NEEDED = 2;

function _checkLanMesh() {
  try {
    const snap = getMeshStatus();
    return snap && snap.status === "lan";
  } catch (e) {
    console.warn("[useOnlineStatus] getMeshStatus error:", e?.message || e);
    return false;
  }
}

export function useOnlineStatus() {
  const [state, setState] = useState(() => ({
    online: typeof navigator !== "undefined" ? navigator.onLine !== false : true,
    source: "nav",
  }));

  // Keep a ref of current state so async callbacks can read without deps
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    let cancelled = false;
    let failStreak = 0;
    let recoveryCount = 0;
    let offlineDebounceTimer = null;
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

    const goOnline = (source) => {
      if (cancelled) return;
      failStreak = 0;
      // When recovering from offline, require consecutive successes
      // to prevent on/off flicker during spotty Wi-Fi.
      if (!stateRef.current.online) {
        recoveryCount += 1;
        if (recoveryCount < RECOVERY_SUCCESSES_NEEDED) return;
      }
      recoveryCount = 0;
      setState({ online: true, source });
    };

    const goOffline = (source) => {
      if (cancelled) return;
      recoveryCount = 0;
      setState({ online: false, source });
    };

    // ── Browser online/offline events ───────────────────────────
    const onOnline = () => {
      if (offlineDebounceTimer) {
        clearTimeout(offlineDebounceTimer);
        offlineDebounceTimer = null;
      }
      goOnline("nav");
    };

    const onOffline = () => {
      // Debounce: Android WebViews fire `offline` spuriously on
      // sleep/wake and network-type changes. Wait a few seconds,
      // then re-check LAN mesh before committing to offline state.
      if (offlineDebounceTimer) clearTimeout(offlineDebounceTimer);
      offlineDebounceTimer = setTimeout(() => {
        offlineDebounceTimer = null;
        if (cancelled) return;
        // Re-check LAN mesh after debounce — it may have healed.
        if (_checkLanMesh()) {
          goOnline("lan");
          return;
        }
        // Re-check navigator — the event may have been transient.
        if (typeof navigator !== "undefined" && navigator.onLine !== false) {
          goOnline("nav");
          return;
        }
        goOffline("nav");
      }, OFFLINE_DEBOUNCE_MS);
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    // ── Periodic heartbeat ──────────────────────────────────────
    const beat = async () => {
      if (cancelled) return;
      // LAN mesh is authoritative — always check first.
      if (_checkLanMesh()) {
        goOnline("lan");
        return;
      }
      // Browser says offline → trust it (no need to ping backend).
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        failStreak = 0;
        goOffline("nav");
        return;
      }
      if (!backendUrl) return;
      try {
        const ctl = new AbortController();
        const timer = setTimeout(() => ctl.abort(), HEALTH_TIMEOUT_MS);
        const r = await fetch(`${backendUrl}/api/health`, {
          method: "GET",
          cache: "no-store",
          signal: ctl.signal,
        });
        clearTimeout(timer);
        if (!cancelled) {
          if (r && r.ok) {
            goOnline("heartbeat");
          } else {
            // Non-ok response (e.g. 500) — server is alive but sick.
            // Still counts as "reachable" for offline purposes.
            goOnline("heartbeat");
          }
        }
      } catch (_) {
        if (!cancelled) {
          failStreak += 1;
          if (failStreak >= FAIL_STREAK_THRESHOLD) {
            // Even after multiple backend failures, LAN mesh is
            // authoritative. Re-check before flipping.
            if (_checkLanMesh()) {
              goOnline("lan");
              return;
            }
            goOffline("heartbeat");
          }
          // Below threshold → silently absorb. Next beat will retry.
        }
      }
    };

    const onVis = () => { if (document.visibilityState === "visible") beat(); };
    document.addEventListener("visibilitychange", onVis);
    beat();
    const id = setInterval(beat, HEARTBEAT_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
      if (offlineDebounceTimer) clearTimeout(offlineDebounceTimer);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return state;
}
